import { useReadQuery } from "@apollo/client";
import { GridProps, Typography } from "@mui/material";
import { amber, blue, blueGrey, green, grey, indigo, purple } from "@mui/material/colors";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import DonutChart from "components/Dashboard/DonutChart";
import { SubmissionStatus } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useCallback, useMemo } from "react";

import { useSubmissionDonutLegend } from "./donutLegend";
import { useWasteSubmissionsOverviewContext } from "./WasteSubmissionsOverviewContext";

// Status display configuration with colors matching waste overview palette
// Typed as Record<string, ...> to handle unknown statuses gracefully
// Labels are shortened for chart legend readability
const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  [SubmissionStatus.NotStarted]: {
    label: "Upcoming",
    color: amber[500], // Upcoming/pending work
  },
  [SubmissionStatus.InProgress]: {
    label: "In Progress",
    color: blue[400], // Active work
  },
  [SubmissionStatus.EncampInitialReview]: {
    label: "Encamp Review",
    color: indigo[400], // Staff review (matches generator status palette)
  },
  [SubmissionStatus.InReview]: {
    label: "In Review",
    color: purple[300], // Customer review
  },
  [SubmissionStatus.ProcessingSubmission]: {
    label: "Ready",
    color: blueGrey[400], // Ready for submission
  },
  [SubmissionStatus.FilingComplete]: {
    label: "Submitted",
    color: green[500], // Complete/success
  },
  [SubmissionStatus.NotReporting]: {
    label: "Not Reporting",
    color: grey[400], // N/A state
  },
};

type SubmissionStatusTileProps = GridProps & {
  onStatusClick?: (status: SubmissionStatus) => void;
  selectedStatus?: SubmissionStatus | null;
};

export function SubmissionStatusTile({
  onStatusClick,
  selectedStatus,
  ...gridProps
}: SubmissionStatusTileProps) {
  return (
    <DashboardTile {...gridProps}>
      <SubmissionStatusContent onStatusClick={onStatusClick} selectedStatus={selectedStatus} />
    </DashboardTile>
  );
}

// Export content component for use in unified container
export { SubmissionStatusContent };

function SubmissionStatusContent({
  onStatusClick,
  selectedStatus,
}: Pick<SubmissionStatusTileProps, "onStatusClick" | "selectedStatus">) {
  const { isStaff } = useCurrentUser();
  const { summaryQuery } = useWasteSubmissionsOverviewContext();
  const { legend, legendSx } = useSubmissionDonutLegend();

  const { data } = useReadQuery(summaryQuery);

  const summary = data?.wasteSubmissionsSummary;

  const chartData = useMemo(() => {
    if (!summary?.byStatus || summary.byStatus.length === 0) return [];

    return summary.byStatus
      .filter((item) => {
        // Hide ENCAMP_INITIAL_REVIEW for non-staff users
        if (!isStaff && item.status === SubmissionStatus.EncampInitialReview) {
          return false;
        }
        // Only show statuses with counts
        return item.count > 0;
      })
      .map((item) => {
        // Use fallback for unknown statuses to prevent errors
        const config = STATUS_CONFIG[item.status] ?? {
          label: item.status,
          color: grey[500],
        };
        return {
          id: item.status,
          value: item.count,
          label: `${config.label} (${item.count})`,
          color: config.color,
        };
      });
  }, [summary, isStaff]);

  const totalCount = summary?.total ?? 0;

  const selectedIndex = useMemo(() => {
    if (!selectedStatus) return null;
    return chartData.findIndex((item) => item.id === selectedStatus);
  }, [selectedStatus, chartData]);

  const handleItemClick = useCallback(
    (
      _event: React.MouseEvent<SVGPathElement, MouseEvent>,
      itemIdentifier: { dataIndex: number }
    ) => {
      const clickedItem = chartData[itemIdentifier.dataIndex];
      if (clickedItem && onStatusClick) {
        onStatusClick(clickedItem.id as SubmissionStatus);
      }
    },
    [chartData, onStatusClick]
  );

  const handleLegendItemClick = useCallback(
    (item: { id?: string | number }) => {
      if (item.id && onStatusClick) {
        onStatusClick(item.id as SubmissionStatus);
      }
    },
    [onStatusClick]
  );

  return (
    <>
      <DashboardTile.Header title="Submission Status" infoTooltip={<SubmissionStatusTooltip />} />
      <DashboardTile.ContentArea>
        {chartData.length > 0 ? (
          <DonutChart
            seriesData={chartData}
            containerHeight="250px"
            label={totalCount.toString()}
            subLabel="Total"
            selectedIndex={selectedIndex}
            onItemClick={handleItemClick}
            onLegendItemClick={handleLegendItemClick}
            legend={legend}
            legendSx={legendSx}
            colors={chartData.map((item) => item.color)}
          />
        ) : (
          <Typography color="text.secondary" textAlign="center" py={4}>
            No submissions found
          </Typography>
        )}
      </DashboardTile.ContentArea>
    </>
  );
}

function SubmissionStatusTooltip() {
  return (
    <Typography variant="caption">
      <Typography component="span" variant="caption" fontWeight={800}>
        Submission Status
      </Typography>{" "}
      shows the distribution of your submissions by their current status. Click on a segment to
      filter the list below.
    </Typography>
  );
}
